import { Form, Row } from 'antd';
import React, { useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import FormWrapper from 'components/molecules/FormWrapper';
import FsluzbyIdentifikace from 'components/molecules/StatniSprava/Identifikace';

import { useDateLimit } from 'hooks/statniSprava/useDateLimit';
import { useFormBasics } from 'hooks/statniSprava/useFormBasics';
import usePageName from 'hooks/usePageName';

import typOsobySelect from 'data/codebooks/specialniSluzby/typOsobySelect';

import isinValidation from 'utils/validations/isinValidation';
import numberValidation from 'utils/validations/numberValidation';
import { rcmaValidationSimple } from 'utils/validations/rcmaValidation';

import AlertComponent from './AlertComponent';
import IsinTable from './IsinTable';
import OdeslaniPozadavkuRows from './odeslaniPozadavkuRows';

interface IF28 {}

const F28: React.FC<IF28> = () => {
    const [form] = Form.useForm();
    const pageName = usePageName();
    const [, dateLoading, commonDisabledDate] = useDateLimit();
    const [showIsinTable, setShowIsinTable] = useState<boolean>(false);
    const [, rerender] = useState<boolean>(false);

    const [
        errMsg,
        currentStep,
        idSluzby,
        upozorneni,
        loading,
        odeslaniResp,
        setErrMsg,
        prev,
        onFormSubmit,
        showPxKlicLoadingMsg
    ] = useFormBasics();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (fieldsValue: any) => {
        const dataToSend = {
            ...fieldsValue,
            dtod: fieldsValue.dtod.format('YYYY-MM-DD'),
            dtdo: fieldsValue.dtdo.format('YYYY-MM-DD'),
            ...(fieldsValue.idmb &&
                typeof fieldsValue.idmb === 'object' && {
                    idmb: fieldsValue.idmb.format('YYYYMMDD')
                }),
            ...(pageName === 'page605' && {
                octg: '1'
            })
        };
        onFormSubmit(dataToSend);
    };

    return (
        // page637 page92b page605
        <LoadingWrapper loading={loading} loadingText={showPxKlicLoadingMsg ? 'LOADING_PXKLIC' : 'NACITANI'}>
            <Title level={4} text="DUVERNE_INFO_TITLE_F28" />
            {errMsg ? (
                <>
                    <Paragraph text={errMsg} />
                    <Row>
                        <ButtonComponent
                            id="f28ZpetBtn"
                            type="default"
                            buttontext="ZPET"
                            onClick={() => setErrMsg('')}
                        />
                    </Row>
                </>
            ) : (
                <>
                    {currentStep !== 2 ? (
                        <>
                            {showIsinTable ? (
                                <IsinTable upperForm={form} setShowIsinTable={setShowIsinTable} formIsinName={'cval'} />
                            ) : (
                                <FormWrapper onFinish={onFinish} outForm={form} name="F28">
                                    <LoadingWrapper loading={dateLoading}>
                                        <DatePickerComponent
                                            name="dtod"
                                            label="ME_POZADAVKY_DATUM1"
                                            disabled={currentStep > 0}
                                            disabledDate={current =>
                                                current > form.getFieldValue('dtdo') || commonDisabledDate(current)
                                            }
                                        />
                                        <DatePickerComponent
                                            name="dtdo"
                                            disabled={currentStep > 0}
                                            label="ME_POZADAVKY_DATUM2"
                                            disabledDate={current =>
                                                current < form.getFieldValue('dtod') || commonDisabledDate(current)
                                            }
                                        />
                                    </LoadingWrapper>
                                    {pageName === 'page605' ? (
                                        <>
                                            <InputComponent
                                                disabled={currentStep > 0}
                                                label="ME_POZADAVKY_INDENTIFIKACE_SOUDU"
                                                name="idco"
                                                maxLength={100}
                                            />
                                            <InputComponent
                                                disabled={currentStep > 0}
                                                label="SPISOVA_ZNACKA"
                                                name="actn"
                                                maxLength={30}
                                            />
                                            <FsluzbyIdentifikace
                                                form={form}
                                                disabled={currentStep > 0}
                                                rerender={rerender}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <InputComponent
                                                disabled={currentStep > 0}
                                                label="ME_POZADAVKY_CISLO_JEDNACI"
                                                name="actn"
                                                maxLength={30}
                                            />
                                            <SelectComponent
                                                name="octg"
                                                labelParagraph="ME_POZADAVKY_KATEGORIE_MAJITELE"
                                                labelCol={{
                                                    span: 8
                                                }}
                                                data={typOsobySelect}
                                                defaultValue={typOsobySelect[0].value}
                                                isFormItem={true}
                                                onChange={() => {
                                                    form.setFieldsValue({
                                                        identifikace: undefined
                                                    });
                                                    rerender(pv => !pv);
                                                }}
                                                disabled={currentStep > 0}
                                            />
                                            <FsluzbyIdentifikace
                                                form={form}
                                                disabled={currentStep > 0}
                                                rerender={rerender}
                                            />
                                        </>
                                    )}
                                    <InputComponent
                                        label="ME_POZADAVKY_PREDCISLI_UCTU"
                                        name="cdac"
                                        type="text"
                                        maxLength={4}
                                        rules={numberValidation()}
                                        notRequired={true}
                                        hide={currentStep === 1 && !form.getFieldValue('cdac')}
                                        disabled={currentStep > 0}
                                    />
                                    <InputComponent
                                        disabled={currentStep > 0}
                                        label="CISLO_UCTU"
                                        name="rcma"
                                        rules={rcmaValidationSimple()}
                                        notRequired={true}
                                        type="text"
                                        onChange={() => form.validateFields}
                                        maxLength={12}
                                    />
                                    <InputComponent
                                        disabled={currentStep > 0}
                                        label="ISIN"
                                        name="cval"
                                        type="text"
                                        maxLength={12}
                                        rules={isinValidation()}
                                        capitalize={true}
                                        notRequired={true}
                                        hide={currentStep === 1 && !form.getFieldValue('cval')}
                                    />
                                    {currentStep === 0 && (
                                        <ButtonComponent
                                            id="f28IsinBtn"
                                            buttontext="DUVERNE_INFO_ISIN_BUTTON"
                                            onClick={() => setShowIsinTable(true)}
                                            style={{
                                                position: 'relative',
                                                top: -12
                                            }}
                                        />
                                    )}

                                    <InputComponent
                                        disabled={currentStep > 0}
                                        label={pageName !== 'page637' ? 'PRIJMENI' : 'ME_POZADAVKY_PRIJMENO'}
                                        name="pjma"
                                        type="text"
                                        maxLength={250}
                                        notRequired={
                                            pageName === 'page637' ||
                                            (pageName === 'page92b' && form.getFieldValue('octg') === '2')
                                        }
                                    />
                                    <InputComponent
                                        disabled={currentStep > 0}
                                        label={pageName !== 'page637' ? 'JMENO' : 'ME_POZADAVKY_JMENO'}
                                        name="jmma"
                                        type="text"
                                        maxLength={250}
                                        notRequired={
                                            pageName === 'page637' ||
                                            (pageName === 'page92b' && form.getFieldValue('octg') === '2')
                                        }
                                    />

                                    {currentStep === 0 && (
                                        <ButtonComponent
                                            id="f28OdeslatBtn"
                                            buttontext="ODESLAT"
                                            onClick={() => form.submit()}
                                        />
                                    )}
                                    {currentStep !== 0 && upozorneni && (
                                        <AlertComponent
                                            upozorneni={upozorneni}
                                            type={currentStep === 1 ? 'error' : 'info'}
                                        />
                                    )}
                                    {currentStep === 1 && (
                                        <Row className="button-row">
                                            <ButtonComponent
                                                id="f28ZpetAltBtn"
                                                buttontext="ZPET"
                                                onClick={() => prev()}
                                                type="default"
                                            />
                                            <ButtonComponent
                                                id="f28PotvrditBtn"
                                                buttontext="POTVRDIT"
                                                onClick={() => form.submit()}
                                            />
                                        </Row>
                                    )}
                                </FormWrapper>
                            )}
                        </>
                    ) : (
                        <>
                            <OdeslaniPozadavkuRows idSluzby={idSluzby} datum={odeslaniResp?.datum} />
                            {upozorneni && <AlertComponent upozorneni={upozorneni} type={'info'} />}
                        </>
                    )}
                </>
            )}
        </LoadingWrapper>
    );
};

export default F28;
